import { create } from 'zustand';
import { isEqual } from 'lodash';
import type {
  CompletedConfiguration,
  ConfigStep,
  PlanConfigurationState,
  SelectedAddOn,
  SelectedEquipment,
} from '@t/configuration.types';
import type { Channel } from '@t/productOffering.types';
import type { EditModeConfig } from '@t/editMode.types';
import type { AddOnFilterCategory } from './configurationStore.types';

export const ADD_ON_CATEGORY_ALL: AddOnFilterCategory = { label: 'Category', value: 'Category' };

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface ConfigurationStore {
  // State
  isPopupOpen: boolean;
  activePlanConfig: PlanConfigurationState | null;
  currentStep: ConfigStep;
  selectedEquipment: SelectedEquipment | null;
  selectedAddOns: SelectedAddOn[];
  configError: string | null;
  channelSideModal: { open: boolean; channels: Channel[] };
  addOnFilterCategory: AddOnFilterCategory;

  // Edit mode state
  editMode: EditModeConfig | null;
  originalSelections: CompletedConfiguration | null;

  // Actions
  openPopup: (
    config: PlanConfigurationState,
    initial?: { equipment?: SelectedEquipment | null; addOns?: SelectedAddOn[] }
  ) => void;
  closePopup: () => void;
  saveAndClose: () => void;
  setStep: (step: ConfigStep) => void;
  transitionToAddOns: () => void;
  selectEquipment: (equipmentId: string) => void;
  updateEquipmentQuantity: (equipmentId: string, quantity: number) => void;
  toggleAddOn: (code: string) => void;
  updateAddOnQuantity: (code: string, quantity: number) => void;
  resetSelections: () => void;
  setConfigError: (message: string | null) => void;
  openChannelSideModal: (channels: Channel[]) => void;
  closeChannelSideModal: () => void;
  setAddOnFilterCategory: (category: AddOnFilterCategory) => void;

  // Edit mode actions
  startEditMode: (config: EditModeConfig, currentSelections: CompletedConfiguration) => void;
  exitEditMode: () => void;
  hasUnsavedChanges: () => boolean;
}

// ---------------------------------------------------------------------------
// Selectors (use with useConfigurationStore)
// ---------------------------------------------------------------------------

export const selectAddOnCount = (state: ConfigurationStore): number =>
  state.selectedAddOns.reduce((sum, a) => sum + a.quantity, 0);

export const selectIsEquipmentSelected = (state: ConfigurationStore): boolean => state.selectedEquipment !== null;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useConfigurationStore = create<ConfigurationStore>((set, get) => ({
  // Initial state
  isPopupOpen: false,
  activePlanConfig: null,
  currentStep: 'equipment',
  selectedEquipment: null,
  selectedAddOns: [],
  configError: null,
  channelSideModal: { open: false, channels: [] },
  addOnFilterCategory: ADD_ON_CATEGORY_ALL,

  // Edit mode initial state
  editMode: null,
  originalSelections: null,

  // Actions
  openPopup: (config, initial) => {
    set({
      isPopupOpen: true,
      activePlanConfig: config,
      currentStep: config.hasEquipment ? 'equipment' : 'addOns',
      selectedEquipment: initial?.equipment ?? null,
      selectedAddOns: initial?.addOns ?? [],
      addOnFilterCategory: ADD_ON_CATEGORY_ALL,
    });
  },

  closePopup: () => {
    set({
      isPopupOpen: false,
      activePlanConfig: null,
      currentStep: 'equipment',
      selectedEquipment: null,
      selectedAddOns: [],
      addOnFilterCategory: ADD_ON_CATEGORY_ALL,
    });
  },

  saveAndClose: () => {
    set({
      isPopupOpen: false,
      activePlanConfig: null,
      currentStep: 'equipment' as ConfigStep,
      selectedEquipment: null,
      selectedAddOns: [],
      addOnFilterCategory: ADD_ON_CATEGORY_ALL,
    });
  },

  setStep: (step) => set({ currentStep: step }),

  transitionToAddOns: () => {
    set((state) => {
      const config = state.activePlanConfig;
      if (!config) return { currentStep: 'addOns' as ConfigStep };
      if (config.bundleAddOnPlanId) {
        return {
          currentStep: 'addOns' as ConfigStep,
          activePlanConfig: {
            ...config,
            planId: config.bundleAddOnPlanId,
            planCategory: config.bundleAddOnMemberCategory ?? config.planCategory,
            parentOfferingCode: config.bundleAddOnParentCode ?? config.bundleAddOnPlanId,
          },
        };
      }
      return { currentStep: 'addOns' as ConfigStep };
    });
  },

  selectEquipment: (equipmentId) => set({ selectedEquipment: { id: equipmentId, quantity: 1 } }),

  updateEquipmentQuantity: (equipmentId, quantity) => set({ selectedEquipment: { id: equipmentId, quantity } }),

  toggleAddOn: (code) => {
    set((state) => ({
      selectedAddOns: state.selectedAddOns.some((a) => a.code === code)
        ? state.selectedAddOns.filter((a) => a.code !== code)
        : [...state.selectedAddOns, { code, quantity: 1 }],
    }));
  },

  updateAddOnQuantity: (code, quantity) => {
    set((state) => ({
      selectedAddOns: state.selectedAddOns.map((a) => (a.code === code ? { ...a, quantity } : a)),
    }));
  },

  resetSelections: () => {
    set({ selectedEquipment: null, selectedAddOns: [] });
  },

  setConfigError: (message) => set({ configError: message }),

  openChannelSideModal: (channels) => set({ isPopupOpen: false, channelSideModal: { open: true, channels } }),
  closeChannelSideModal: () => set({ isPopupOpen: true, channelSideModal: { open: false, channels: [] } }),

  setAddOnFilterCategory: (category) => set({ addOnFilterCategory: category }),

  // Edit mode actions
  startEditMode: (config, currentSelections) => {
    set({
      editMode: config,
      originalSelections: structuredClone(currentSelections),
    });
  },

  exitEditMode: () => {
    set({ editMode: null, originalSelections: null });
  },

  hasUnsavedChanges: () => {
    const { originalSelections, selectedEquipment, selectedAddOns } = get();
    if (!originalSelections) return false;

    return (
      !isEqual(originalSelections.selectedEquipment, selectedEquipment) ||
      !isEqual(originalSelections.selectedAddOns, selectedAddOns)
    );
  },
}));
